

import React, { useRef, useEffect, useCallback } from "react";
import heartLoading from "../../assests/heartLoading.gif";
import RuleTableBody from "../../component/DataMonitor/RuleTableBody";
import RuleTableHead from "../../component/DataMonitor/RuleTableHead";
import RuleTableHeader from "../../component/DataMonitor/RuleTableHeader";


const Tables = ({
  tableData = [],
  loading = false,
  ruleCount = 0,
  error = null,
  totalRecords = 0,
  loadMore = () => {},
  hasMore = false,
  isFetchingMore = false,
  onSearchSubmit = () => {},
  onReset = () => {},
  onColumnSearchChange = () => {},
  columnSearch = {},
  onSortChange = () => {},
}) => {
  const items = [
    { color: "#FB0301", label: "< 90%" },
    { color: "#FFC100", label: "90% – 95%" },
    { color: "#009F52", label: "≥ 95%" },
  ];
  const tableContainerRef = useRef(null);
  const hasMoreRef = useRef(hasMore);
  const isFetchingMoreRef = useRef(isFetchingMore);
  const [sortColumn, setSortColumn] = React.useState(null);
  const [sortOrder, setSortOrder] = React.useState("asc");

  useEffect(() => { hasMoreRef.current = hasMore; }, [hasMore]);
  useEffect(() => { isFetchingMoreRef.current = isFetchingMore; }, [isFetchingMore]);

  const handleSort = (column) => {
    let newOrder = "asc";
    if (sortColumn === column && sortOrder === "asc") {
      newOrder = "desc";
    }
    setSortColumn(column);
    setSortOrder(newOrder);
    if (typeof onSortChange === "function") {
      onSortChange(column, newOrder);
    }
  };

  const handleResetInputs = () => {
    if (typeof onColumnSearchChange === "function") {
      Object.keys(columnSearch).forEach((col) => onColumnSearchChange(col, ""));
    }
    onReset();
  };


  const handleScroll = useCallback(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    if (
      scrollTop + clientHeight >= scrollHeight * 0.5 &&
      hasMoreRef.current &&
      !isFetchingMoreRef.current
    ) {
      loadMore();
    }
  }, [loadMore]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const renderHighlightedText = (text, searchTerm) => {
    if (!searchTerm || !text) return text;

    const parts = text.split(new RegExp(`(${searchTerm})`, "gi"));
    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase() ? (
        <span key={i} style={{ backgroundColor: "orange", fontWeight: "500" }}>
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const truncateText = (text, maxLength = 30) =>
    text && text.length > maxLength ? `${text.slice(0, maxLength)}...` : text || "NA";
  const truncateTexts = (text, maxLength = 15) =>
    text && text.length > maxLength ? `${text.slice(0, maxLength)}...` : text || "NA";

  if (error) {
    return <div className="p-4 text-red-600">Error: {error}</div>;
  }

  return (
    <div className="poppins-regular">
      <RuleTableHeader
        items={items}
        totalRecords={totalRecords}
        loadedCount={tableData.length}
        handleResetInputs={handleResetInputs}
        tableContainerRef={tableContainerRef}
        ruleCount={ruleCount}
      />
      <div
        ref={tableContainerRef}
        style={{ overflowX: "auto", overflowY: "auto", maxHeight: "600px", scrollBehavior: "smooth" }}
      >
        <table className="min-w-full border-collapse border border-gray-200 text-sm">
          <RuleTableHead
            sortColumn={sortColumn}
            sortOrder={sortOrder}
            onSearchSubmit={onSearchSubmit}
            handleSort={handleSort}
            columnSearch={columnSearch}
            onColumnSearchChange={onColumnSearchChange}
          />
          <RuleTableBody
            tableData={tableData}
            loading={loading}
            columnSearch={columnSearch}
            renderHighlightedText={renderHighlightedText}
            truncateText={truncateText}
            truncateTexts={truncateTexts}
          />
        </table>
      </div>

      {isFetchingMore && (
        <div className="flex items-center justify-center py-6 gap-2">
          <img src={heartLoading} alt="Loading more..." style={{ width : 200}} />
          <span className="text-gray-500 text-2xl">Loading more...</span>
        </div>
      )}
      {!hasMore && tableData.length > 0 && (
        <div className="text-center py-3 text-gray-400 text-xs">
          All {totalRecords} records loaded
        </div>
      )}
    </div>
  );
};

export default Tables;
